import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useJobsite } from "../context/JobsiteContext";
import Sheet from "../components/Sheet";
import UserAvatar from "../components/UserAvatar";
import { grossSecondsOf } from "../lib/timecard";
import { fmtDate } from "../lib/utils";
import {
  AlertOctagon,
  AlertTriangle,
  Building2,
  CalendarClock,
  CalendarRange,
  ChevronRight,
  ClipboardCheck,
  Clock,
  FileText,
  LogIn,
  MapPin,
  Megaphone,
  MessageSquare,
  Plus,
  RefreshCw,
  ScrollText,
  Square,
  UserPlus,
  Users,
  Wrench,
  Link2,
  FolderOpen,
} from "lucide-react";

/* ---------- ajudantes ---------- */

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function fmtClock(totalSeconds) {
  const h = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const s = String(totalSeconds % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

const PRIORITY_RANK = { urgent: 0, important: 1, normal: 2 };
const PRIORITY_STYLE = {
  urgent: "border-red-500/50 bg-red-500/10",
  important: "border-amber-500/50 bg-amber-500/10",
  normal: "border-border bg-card",
};
const PRIORITY_LABEL = { urgent: "Urgent", important: "Important", normal: "" };
const STATUS_STYLE = {
  Draft: "border-border bg-muted text-muted-foreground",
  "In Progress": "border-amber-500/40 bg-amber-500/10 text-amber-500",
  "In Review": "border-blue-500/40 bg-blue-500/10 text-blue-400",
  Closed: "border-green-500/40 bg-green-500/10 text-green-500",
};

function Section({ title, action, onAction, children }) {
  return (
    <section className="mt-7">
      <div className="mb-3 flex items-end justify-between">
        <h2 className="text-xl font-bold tracking-tight">{title}</h2>
        {action && (
          <button type="button" onClick={onAction} className="text-sm font-semibold text-blue-400">
            {action}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function Tile({ icon: Icon, label, hint, badge, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex min-h-[112px] flex-col justify-between rounded-2xl border border-border bg-card p-4 text-left active:bg-accent"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <Icon className="h-7 w-7 text-primary" />
        {badge > 0 && (
          <span className="rounded-full bg-primary px-2.5 py-0.5 text-xs font-bold text-primary-foreground">{badge > 99 ? "99+" : badge}</span>
        )}
      </div>
      <div>
        <div className="text-base font-bold leading-tight">{label}</div>
        {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
      </div>
    </button>
  );
}

function AttentionRow({ icon: Icon, text, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 text-left active:opacity-80"
      data-testid={testId}
    >
      <Icon className="h-6 w-6 shrink-0 text-amber-500" />
      <span className="flex-1 text-base font-semibold">{text}</span>
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
    </button>
  );
}

/* ---------- pagina ---------- */

export default function DashboardMobile() {
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const { activeSite } = useJobsite();
  const isStaff = user?.role === "admin" || user?.role === "supervisor" || !!user?.is_master_admin;
  const isAdmin = user?.role === "admin" || !!user?.is_master_admin;

  const [current, setCurrent] = useState(null);
  const [summary, setSummary] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [timecards, setTimecards] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);

  const now = useNow(!!current);
  const siteId = activeSite ? activeSite.id : null;

  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      api.get("/attendance/current"),
      api.get("/dashboard/summary"),
      api.get("/announcements"),
      api.get("/itp/inspections", { params: siteId ? { project_id: siteId } : {} }),
      api.get("/attendance/history", { params: { status: isStaff ? "pending" : "rejected", limit: 200 } }),
    ]);
    const val = (i) => (results[i].status === "fulfilled" ? results[i].value.data : null);
    setCurrent(val(0) || null);
    setSummary(val(1));
    setAnnouncements(Array.isArray(val(2)) ? val(2) : []);
    setInspections(Array.isArray(val(3)) ? val(3) : []);
    setTimecards(Array.isArray(val(4)) ? val(4) : []);
    setUpdatedAt(new Date());
    setLoaded(true);
  }, [siteId, isStaff]);

  useEffect(() => {
    setLoaded(false);
    load();
  }, [load]);

  const go = (path) => () => navigate(path);

  /* projeto */
  const address = activeSite ? [activeSite.address, activeSite.state, activeSite.postcode].filter(Boolean).join(", ") : "";
  const teamCount = activeSite && Array.isArray(activeSite.team) ? activeSite.team.length : 0;
  const linkCount = activeSite && Array.isArray(activeSite.links) ? activeSite.links.length : 0;
  const siteCrew = useMemo(() => {
    const list = (summary && summary.onsite_users) || [];
    if (!activeSite) return list;
    return list.filter((u) => u.jobsite_name === activeSite.name);
  }, [summary, activeSite]);

  /* avisos: so os da obra atual (ou de todas), sem os vencidos; urgentes primeiro */
  const notices = useMemo(() => {
    const nowMs = Date.now();
    return announcements
      .filter((a) => {
        if (a.expires_at && Date.parse(a.expires_at) < nowMs) return false;
        const ids = Array.isArray(a.jobsite_ids) ? a.jobsite_ids : [];
        return ids.length === 0 || !siteId || ids.includes(siteId);
      })
      .sort(
        (a, b) =>
          (PRIORITY_RANK[a.priority] ?? 2) - (PRIORITY_RANK[b.priority] ?? 2) ||
          String(b.created_at).localeCompare(String(a.created_at))
      )
      .slice(0, 2);
  }, [announcements, siteId]);

  /* inspecoes abertas (as mais mexidas por ultimo) */
  const openInspections = useMemo(
    () =>
      inspections
        .filter((i) => (i.status || "Draft") !== "Closed")
        .sort((a, b) => String(b.updated_at || b.created_at).localeCompare(String(a.updated_at || a.created_at))),
    [inspections]
  );

  const pendingUsers = (summary && summary.pending_users) || 0;
  const elapsed = current ? fmtClock(grossSecondsOf(current, now)) : null;

  /* ferramentas */
  const tools = [
    { id: "time", icon: Clock, label: "My Time", hint: "Clock in and out", to: "/app/attendance" },
    { id: "inspections", icon: ClipboardCheck, label: "Inspections", hint: "ITPs and checklists", to: "/app/inspections", badge: openInspections.length },
    { id: "chat", icon: MessageSquare, label: "Chat", hint: "Jobsite messages", to: "/app/chat" },
    { id: "logs", icon: ScrollText, label: "Daily Logs", hint: "Field notes", to: "/app/logs" },
    { id: "photos", icon: FolderOpen, label: "Documents", hint: "Folders and photos", to: "/app/photos" },
    { id: "announcements", icon: Megaphone, label: "Announcements", hint: "Notices", to: "/app/announcements" },
    { id: "shifts", icon: CalendarRange, label: "My Shifts", hint: "Upcoming shifts", to: "/app/my-shifts" },
    { id: "forms", icon: FileText, label: "Forms", hint: "Fill and sign", to: "/app/forms" },
  ];
  const adminTools = [
    { id: "tracking", icon: CalendarClock, label: "Time Tracking", hint: "Approve timecards", to: "/app/admin/time-tracking", badge: timecards.length },
    { id: "users", icon: Users, label: "Users & Access", hint: "People and roles", to: "/app/admin/users", badge: pendingUsers },
    { id: "sites", icon: MapPin, label: "Jobsites", hint: "Projects and locations", to: "/app/admin/jobsites" },
    { id: "roster", icon: CalendarRange, label: "Roster", hint: "Shifts and crews", to: "/app/admin/roster" },
    { id: "itp", icon: Wrench, label: "ITP Templates", hint: "Checklists", to: "/app/admin/itp-templates" },
    { id: "ncr", icon: AlertOctagon, label: "NCRs", hint: "Non-conformances", to: "/app/admin/ncrs" },
    ...(isAdmin ? [{ id: "employees", icon: UserPlus, label: "Employees", hint: "Add and manage", to: "/app/admin/employees" }] : []),
  ];

  const quick = [
    current
      ? { icon: Square, label: "Clock out", hint: "End your shift", to: "/app/attendance" }
      : { icon: LogIn, label: "Clock in", hint: "Start your day", to: "/app/attendance" },
    { icon: ScrollText, label: "New daily log", hint: "Notes and photos", to: "/app/logs" },
    { icon: MessageSquare, label: "Open chat", hint: "Message the crew", to: "/app/chat" },
    ...(isStaff
      ? [
          { icon: ClipboardCheck, label: "Start an inspection", hint: "Pick a template", to: "/app/inspections" },
          { icon: Megaphone, label: "Post an announcement", hint: "Send a notice", to: "/app/announcements" },
        ]
      : []),
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 pb-32 pt-5">
      <div className="text-sm text-muted-foreground">
        {greeting()}, {user?.name?.split(" ")[0] || "there"}
      </div>
      <h1 className="mt-0.5 text-3xl font-bold tracking-tight">Project overview</h1>

      <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
        <span>{updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "Loading..."}</span>
        <button type="button" onClick={load} className="inline-flex items-center gap-1 hover:text-foreground">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Projeto */}
      <div
        className={`mt-4 rounded-2xl border border-border bg-card p-4 ${activeSite ? "cursor-pointer active:opacity-90" : ""}`}
        onClick={activeSite ? go("/app/project") : undefined}
        onKeyDown={(e) => {
          if (activeSite && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            navigate("/app/project");
          }
        }}
        role={activeSite ? "button" : undefined}
        tabIndex={activeSite ? 0 : undefined}
        data-testid="home-project"
      >
        {activeSite ? (
          <>
            <div className="flex gap-4">
              <div className="grid h-20 w-20 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary/30 to-primary/5 text-primary">
                <Building2 className="h-9 w-9" />
              </div>
              <div className="min-w-0 flex-1">
                {company?.name && <div className="truncate text-sm text-muted-foreground">{company.name}</div>}
                <div className="line-clamp-2 text-lg font-bold leading-snug">{activeSite.name}</div>
                {address && <div className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{address}</div>}
              </div>
              <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {linkCount > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm font-medium" data-testid="home-links-chip">
                  <Link2 className="h-4 w-4" /> {linkCount} project {linkCount === 1 ? "link" : "links"}
                </span>
              )}
              {teamCount > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm font-medium" data-testid="home-team-chip">
                  <Users className="h-4 w-4" /> {teamCount} team {teamCount === 1 ? "member" : "members"}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm font-medium">
                <ClipboardCheck className="h-4 w-4" /> {openInspections.length} open {openInspections.length === 1 ? "inspection" : "inspections"}
              </span>
              {isStaff && (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-sm font-medium">
                  <Users className="h-4 w-4" /> {siteCrew.length} on site
                </span>
              )}
            </div>
            {isStaff && siteCrew.length > 0 && (
              <div className="mt-3 flex items-center">
                {siteCrew.slice(0, 7).map((u, i) => (
                  <div key={u.id} className={i === 0 ? "" : "-ml-2"}>
                    <UserAvatar url={u.avatar_url} name={u.name} size={36} className="ring-2 ring-card" />
                  </div>
                ))}
                {siteCrew.length > 7 && <span className="ml-2 text-sm text-muted-foreground">+{siteCrew.length - 7}</span>}
              </div>
            )}
          </>
        ) : (
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-muted text-muted-foreground">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <div className="text-base font-bold">No project selected</div>
              <div className="mt-0.5 text-sm text-muted-foreground">Choose a project in the bar at the top of the screen.</div>
            </div>
          </div>
        )}
      </div>

      {/* Ponto */}
      <button
        type="button"
        onClick={go("/app/attendance")}
        className={`relative mt-4 block w-full overflow-hidden rounded-2xl border p-4 text-left active:opacity-90 ${
          current ? "border-primary/40 bg-card" : "border-border bg-card"
        }`}
        data-testid="home-clock"
      >
        {current && <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent" />}
        <div className="relative flex items-center gap-4">
          <div className={`grid h-14 w-14 shrink-0 place-items-center rounded-xl ${current ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
            {current ? <Clock className="h-7 w-7" /> : <LogIn className="h-7 w-7" />}
          </div>
          <div className="min-w-0 flex-1">
            {current ? (
              <>
                <div className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-primary">On the clock</div>
                <div className="font-mono text-3xl font-bold tracking-tight">{elapsed}</div>
                <div className="truncate text-sm text-muted-foreground">{current.jobsite_name}</div>
              </>
            ) : (
              <>
                <div className="text-lg font-bold">You are not clocked in</div>
                <div className="text-sm text-muted-foreground">Tap to start your day</div>
              </>
            )}
          </div>
          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
        </div>
      </button>

      {/* Precisa de atencao */}
      {loaded && (isStaff ? timecards.length > 0 || pendingUsers > 0 : timecards.length > 0) && (
        <Section title="Needs your attention">
          <div className="space-y-2">
            {isStaff && timecards.length > 0 && (
              <AttentionRow
                icon={CalendarClock}
                text={`${timecards.length} ${timecards.length === 1 ? "timecard" : "timecards"} awaiting approval`}
                onClick={go("/app/admin/time-tracking")}
                testId="home-attn-timecards"
              />
            )}
            {isStaff && pendingUsers > 0 && (
              <AttentionRow
                icon={Users}
                text={`${pendingUsers} ${pendingUsers === 1 ? "person" : "people"} waiting for approval`}
                onClick={go("/app/admin/users")}
                testId="home-attn-users"
              />
            )}
            {!isStaff && timecards.length > 0 && (
              <AttentionRow
                icon={AlertTriangle}
                text={`${timecards.length} ${timecards.length === 1 ? "timecard was" : "timecards were"} sent back to you`}
                onClick={go("/app/attendance")}
                testId="home-attn-rejected"
              />
            )}
          </div>
        </Section>
      )}

      {/* Avisos */}
      {notices.length > 0 && (
        <Section title="Announcements" action="See all" onAction={go("/app/announcements")}>
          <div className="space-y-3">
            {notices.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={go("/app/announcements")}
                className={`block w-full rounded-xl border p-4 text-left active:opacity-80 ${PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.normal}`}
              >
                {PRIORITY_LABEL[a.priority] && (
                  <div className={`mb-1 font-mono text-[10px] font-bold uppercase tracking-widest ${a.priority === "urgent" ? "text-red-400" : "text-amber-500"}`}>
                    {PRIORITY_LABEL[a.priority]}
                  </div>
                )}
                <div className="text-base font-bold leading-snug">{a.title}</div>
                {a.body && <div className="mt-1 line-clamp-2 text-sm text-muted-foreground">{a.body}</div>}
                <div className="mt-2 text-xs text-muted-foreground">
                  {a.author ? `${a.author} | ` : ""}
                  {fmtDate(a.created_at)}
                </div>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Inspecoes abertas */}
      {openInspections.length > 0 && (
        <Section title="Open inspections" action="See all" onAction={go("/app/inspections")}>
          <div className="space-y-3">
            {openInspections.slice(0, 3).map((i) => {
              const total = typeof i.items_count === "number" ? i.items_count : 0;
              const done = i.inspected_count || 0;
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              const st = i.status || "Draft";
              return (
                <button
                  key={i.id}
                  type="button"
                  onClick={go(`/app/inspections/${i.id}`)}
                  className="block w-full rounded-xl border border-border bg-card p-4 text-left active:bg-accent"
                  data-testid={`home-insp-${i.id}`}
                >
                  <div className="line-clamp-2 text-base font-bold leading-snug">{i.title || "Untitled"}</div>
                  <div className="mt-0.5 text-sm text-muted-foreground">{i.location_details || "No location"}</div>
                  {total > 0 && (
                    <div className="mt-3 flex items-center gap-2">
                      <div className="h-2 w-28 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-green-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-mono text-xs text-muted-foreground">
                        {done}/{total}
                      </span>
                    </div>
                  )}
                  <div className="mt-3 flex items-center justify-between">
                    <span className={`rounded-full border px-3 py-1 text-sm font-semibold ${STATUS_STYLE[st] || STATUS_STYLE.Draft}`}>{st}</span>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {/* Ferramentas */}
      <Section title="Tools">
        <div className="grid grid-cols-2 gap-3">
          {tools.map((t) => (
            <Tile key={t.id} icon={t.icon} label={t.label} hint={t.hint} badge={t.badge} onClick={go(t.to)} testId={`tool-${t.id}`} />
          ))}
        </div>
      </Section>

      {isStaff && (
        <Section title="Administration">
          <div className="grid grid-cols-2 gap-3">
            {adminTools.map((t) => (
              <Tile key={t.id} icon={t.icon} label={t.label} hint={t.hint} badge={t.badge} onClick={go(t.to)} testId={`tool-${t.id}`} />
            ))}
          </div>
        </Section>
      )}

      {/* Botao + */}
      <button
        type="button"
        onClick={() => setQuickOpen(true)}
        aria-label="Quick actions"
        className="fixed right-4 z-30 grid h-16 w-16 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-xl active:scale-95"
        style={{ bottom: "calc(5.5rem + env(safe-area-inset-bottom))" }}
        data-testid="home-fab"
      >
        <Plus className="h-8 w-8" />
      </button>

      {quickOpen && (
        <Sheet title="Quick actions" onClose={() => setQuickOpen(false)}>
          <div className="-mx-5 -my-5 divide-y divide-border">
            {quick.map((q) => (
              <button
                key={q.label}
                type="button"
                onClick={() => {
                  setQuickOpen(false);
                  navigate(q.to);
                }}
                className="flex w-full items-center gap-4 px-5 py-4 text-left active:bg-accent"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
                  <q.icon className="h-6 w-6" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-base font-semibold">{q.label}</span>
                  <span className="block text-sm text-muted-foreground">{q.hint}</span>
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </div>
  );
}
